import { StateCode } from '@/types/schema';

export interface DiagramTaxTileTitleInputs {
  /** True when there is federal estate tax > 0 on this death. */
  federal: boolean;
  /** Set when there is residential state estate tax > 0; null otherwise. */
  residentialStateCode: StateCode | null;
  /** Non-residential state codes with estate tax > 0. */
  nonResidentialStateCodes: StateCode[];
  /** True when there is GST tax > 0 on this death. */
  gst: boolean;
  /** True when there is any state inheritance tax > 0 on this death. */
  inheritance: boolean;
}

// Spec: https://www.notion.so/withluminary/33465ff1a4e280f49007e7a8344acac7
// Returns null for unenumerated cases so callers can fall back to legacy.
export function getDiagramTaxTileTitle({
  federal,
  residentialStateCode,
  nonResidentialStateCodes,
  gst,
  inheritance,
}: DiagramTaxTileTitleInputs): string | null {
  const hasResidentialState = residentialStateCode !== null;
  const hasNonResidentialState = nonResidentialStateCodes.length > 0;

  const presentCount = [
    federal,
    hasResidentialState,
    hasNonResidentialState,
    gst,
    inheritance,
  ].filter(Boolean).length;

  if (presentCount === 0) return null;

  if (presentCount === 1) {
    if (federal) return 'Federal estate tax';
    return null;
  }

  if (presentCount === 2 && federal) {
    if (hasResidentialState) {
      return `Federal & ${residentialStateCode} estate tax`;
    }
    if (hasNonResidentialState) {
      return nonResidentialStateCodes.length === 1
        ? `Federal & ${nonResidentialStateCodes[0]} taxes`
        : 'Federal & state estate taxes';
    }
    if (gst) return 'Federal estate & GST';
    if (inheritance) return 'Federal estate & inheritance tax';
  }

  // "Federal, state & GST" wins whenever federal + residential state + GST are all present.
  if (federal && hasResidentialState && gst) {
    return 'Federal, state & GST';
  }

  if (presentCount === 3 && federal) {
    if (hasResidentialState && hasNonResidentialState) {
      return 'Federal & state estate taxes';
    }
    if (hasResidentialState && inheritance) {
      return 'Federal & state estate taxes';
    }
    if (gst && inheritance && !hasResidentialState && !hasNonResidentialState) {
      return 'Federal, GST & inheritance tax';
    }
  }

  if (!federal) return null;
  return 'Federal & state estate taxes';
}
